import React from 'react';
import { createPortal } from 'react-dom';
import { X, Building2, MapPin, Phone, Mail, Calendar, Clock, Navigation, Map, FileText, Users } from 'lucide-react';

export default function ClienteDetalleModal({ cliente, onClose, onEditar }) {
  if (!cliente) return null;

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    try {
      return new Date(fecha).toLocaleDateString('es-PY', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatearHora = (hora) => {
    if (!hora) return 'N/A';
    return hora;
  };

  const obtenerEstadoBadge = (estado) => {
    const estados = {
      activo: 'bg-green-100 text-green-800',
      inactivo: 'bg-gray-100 text-gray-800',
      suspendido: 'bg-red-100 text-red-800',
      garantia: 'bg-emerald-100 text-emerald-800',
      prospecto: 'bg-yellow-100 text-yellow-800'
    };
    return estados[estado] || 'bg-gray-100 text-gray-800';
  };

  const diasSemana = [
    { key: 'lunes', nombre: 'Lunes', abrev: 'L' },
    { key: 'martes', nombre: 'Martes', abrev: 'M' },
    { key: 'miercoles', nombre: 'Miércoles', abrev: 'X' },
    { key: 'jueves', nombre: 'Jueves', abrev: 'J' },
    { key: 'viernes', nombre: 'Viernes', abrev: 'V' },
    { key: 'sabado', nombre: 'Sábado', abrev: 'S' },
    { key: 'domingo', nombre: 'Domingo', abrev: 'D' }
  ];

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-purple-600 px-6 py-5 flex justify-between items-start">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div className="text-white">
              <h2 className="text-2xl font-bold mb-1">{cliente.nombre}</h2>
              <div className="flex items-center gap-3 text-sm">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${obtenerEstadoBadge(cliente.estado)}`}>
                  {(cliente.estado || 'activo').toUpperCase()}
                </span>
                <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium">
                  {(cliente.tipo || 'empresa').toUpperCase()}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onEditar}
              className="px-4 py-2 bg-white text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors font-medium text-sm flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Editar
            </button>
            <button
              onClick={onClose}
              className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Información de Contacto */}
            <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-xl p-5 border border-emerald-100">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5 text-emerald-600" />
                Información de Contacto
              </h3>
              <div className="space-y-3">
                {cliente.telefono && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-gray-500 font-medium">Teléfono</div>
                      <div className="text-sm text-gray-900 font-medium">{cliente.telefono}</div>
                    </div>
                  </div>
                )}
                {cliente.email && (
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-gray-500 font-medium">Email</div>
                      <div className="text-sm text-gray-900 font-medium break-all">{cliente.email}</div>
                    </div>
                  </div>
                )}
                {cliente.ruc && (
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-gray-500 font-medium">RUC</div>
                      <div className="text-sm text-gray-900 font-medium">{cliente.ruc}</div>
                    </div>
                  </div>
                )}
                {(cliente.direccion || cliente.ciudad) && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-gray-500 font-medium">Dirección</div>
                      <div className="text-sm text-gray-900">
                        {cliente.direccion && <div>{cliente.direccion}</div>}
                        {cliente.ciudad && <div className="text-gray-600">{cliente.ciudad}</div>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Ubicación Geográfica */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-100">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Navigation className="w-5 h-5 text-green-600" />
                Ubicación Geográfica
              </h3>
              <div className="space-y-3">
                {(cliente.ubicacion_ciudad || cliente.ubicacion_departamento || cliente.ubicacion_pais) && (
                  <div className="flex items-start gap-3">
                    <Map className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-gray-500 font-medium">Ubicación</div>
                      <div className="text-sm text-gray-900">
                        {cliente.ubicacion_ciudad && <div className="font-medium">{cliente.ubicacion_ciudad}</div>}
                        {cliente.ubicacion_departamento && <div className="text-gray-600">{cliente.ubicacion_departamento}</div>}
                        {cliente.ubicacion_pais && <div className="text-gray-500 text-xs">{cliente.ubicacion_pais}</div>}
                      </div>
                    </div>
                  </div>
                )}
                {(cliente.coordenadas_lat && cliente.coordenadas_lng) && (
                  <div className="flex items-start gap-3">
                    <Navigation className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-gray-500 font-medium">Coordenadas GPS</div>
                      <div className="text-sm text-gray-900 font-mono">
                        {Number(cliente.coordenadas_lat).toFixed(6)}, {Number(cliente.coordenadas_lng).toFixed(6)}
                      </div>
                      <a
                        href={`https://www.google.com/maps?q=${cliente.coordenadas_lat},${cliente.coordenadas_lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-emerald-600 hover:text-emerald-700 underline inline-flex items-center gap-1 mt-1"
                      >
                        <MapPin className="w-3 h-3" />
                        Ver en Google Maps
                      </a>
                    </div>
                  </div>
                )}
                {!cliente.ubicacion_ciudad && !cliente.coordenadas_lat && (
                  <div className="text-sm text-gray-400 italic">
                    No hay información de ubicación geográfica
                  </div>
                )}
              </div>
            </div>

            {/* Información Contractual */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-100">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Información Contractual
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white/60 rounded-lg">
                  <span className="text-sm text-gray-600">Horas Contratadas</span>
                  <span className="text-lg font-bold text-purple-600">{cliente.horas_contratadas || 0}h</span>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 font-medium">Fecha de Creación</div>
                    <div className="text-sm text-gray-900">{formatearFecha(cliente.created_at)}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 font-medium">Última Actualización</div>
                    <div className="text-sm text-gray-900">{formatearFecha(cliente.updated_at)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Horarios Laborales */}
            {cliente.horarios_laborales && (
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-600" />
                  Horarios Laborales
                </h3>
                <div className="grid grid-cols-7 gap-2">
                  {diasSemana.map(dia => {
                    const horario = cliente.horarios_laborales[dia.key];
                    const activo = horario?.activo;
                    return (
                      <div key={dia.key} className={`text-center p-2 rounded-lg ${activo ? 'bg-green-100 border border-green-300' : 'bg-gray-100 border border-gray-200'}`}>
                        <div className={`text-xs font-bold ${activo ? 'text-green-700' : 'text-gray-400'}`}>
                          {dia.abrev}
                        </div>
                        {activo && horario && (
                          <div className="text-[10px] text-green-600 mt-1">
                            {formatearHora(horario.hora_inicio)}<br/>
                            {formatearHora(horario.hora_fin)}
                          </div>
                        )}
                        {!activo && <div className="text-[10px] text-gray-400 mt-1">-</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sucursales */}
            {cliente.sucursales && cliente.sucursales.length > 0 && (
              <div className="lg:col-span-2 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-5 border border-teal-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-teal-600" />
                  Sucursales ({cliente.sucursales.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {cliente.sucursales.map((sucursal, idx) => {
                    const nombre = typeof sucursal === 'string' ? sucursal : sucursal.nombre;
                    const ciudad = sucursal.ubicacion_ciudad;
                    const direccion = sucursal.ubicacion_direccion;
                    const lat = sucursal.coordenadas_lat;
                    const lng = sucursal.coordenadas_lng;
                    const remota = sucursal.ubicacion_remota;
                    
                    return (
                      <div key={idx} className="bg-white rounded-lg p-3 border border-teal-200 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-2">
                          <div className="font-semibold text-gray-800 text-sm flex items-center gap-1">
                            <MapPin className="w-4 h-4 text-teal-600" />
                            {nombre}
                          </div>
                          {remota && (
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                              Remota
                            </span>
                          )}
                        </div>
                        {ciudad && <div className="text-xs text-gray-600 mb-1">📍 {ciudad}</div>}
                        {direccion && <div className="text-xs text-gray-500 mb-1">{direccion}</div>}
                        {lat && lng && (
                          <div className="text-[10px] text-gray-400 font-mono mt-2">
                            {Number(lat).toFixed(4)}, {Number(lng).toFixed(4)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Observaciones */}
            {cliente.observaciones && (
              <div className="lg:col-span-2 bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-5 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gray-600" />
                  Observaciones
                </h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{cliente.observaciones}</p>
              </div>
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium"
          >
            Cerrar
          </button>
          <button
            onClick={onEditar}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Editar Cliente
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
